<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  active: { type: Boolean, default: false },
  color: { type: String, default: '#9C9C9C' },
  count: { type: Number, default: 0 },
});

function parseHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const h = hex.replace('#', '');
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  return [r, g, b];
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0;
  let sat = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      case b: hue = (r - g) / d + 4; break;
    }
    hue *= 60;
  }
  return [Math.round(hue), Math.round(sat * 100), Math.round(l * 100)];
}

function rgba(rgb, a) {
  if (!rgb) return `rgba(156, 156, 156, ${a})`;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;
}

function mixBlack(rgb, ratio) {
  if (!rgb) return '#000000';
  const r = Math.round(rgb[0] * ratio);
  const g = Math.round(rgb[1] * ratio);
  const b = Math.round(rgb[2] * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

// Paleta — cores NÃO mudam entre active e nao-active.
// O destaque do active vem do wrapper externo (mais padding e bg da cor),
// nao da mudanca de cor do chip.
const palette = computed(() => {
  const rgb = parseHex(props.color);
  const hsl = rgb ? rgbToHsl(...rgb) : [0, 0, 50];
  const [h, s] = hsl;
  const sLight = Math.max(60, Math.min(80, s));
  return {
    // LIGHT
    bg:        `hsl(${h}, ${sLight}%, 97%)`,
    counterBg: `hsl(${h}, ${sLight}%, 89%)`,
    accent:    `hsl(${h}, ${sLight}%, 59%)`,
    // DARK — manter MUITO sutil (mesma matiz, saturacao baixa e L baixa) pra
    // a sidebar nao virar uma colcha de retalhos colorida em dark mode.
    bgDark:        `hsl(${h}, ${Math.max(8, Math.round(s * 0.12))}%, 17%)`,
    counterBgDark: rgba(rgb, 0.16),
    accentDark:    `hsl(${h}, ${sLight}%, 72%)`,
  };
});

const chipStyle = computed(() => ({
  '--sz-chip-bg': palette.value.bg,
  '--sz-chip-counter-bg': palette.value.counterBg,
  '--sz-chip-accent': palette.value.accent,
  '--sz-chip-bg-dark': palette.value.bgDark,
  '--sz-chip-counter-bg-dark': palette.value.counterBgDark,
  '--sz-chip-accent-dark': palette.value.accentDark,
}));
</script>

<template>
  <!-- Wrapper externo: cresce em active com padding + bg da cor (mesmo tom do chip).
       CSS vars definidas aqui ficam acessiveis tanto pro wrap (active) quanto
       pro chip filho. -->
  <span
    class="sz-label-chip-outer"
    :class="{ 'is-active': active }"
    :style="chipStyle"
  >
    <span class="sz-label-chip">
      <span class="sz-label-chip-inner">
        <span class="sz-label-chip-dot-wrap">
          <span class="sz-label-chip-dot" />
        </span>
        <span class="sz-label-chip-text">{{ label }}</span>
      </span>
      <span v-if="count > 0" class="sz-label-chip-counter">{{ count }}</span>
    </span>
  </span>
</template>

<style scoped>
/* Outer wrap: por padrao ocupa o espaco do leaf, sem decoracao.
   Quando active, ganha padding + bg na mesma cor do chip (efeito "container maior"
   do Figma — mesma cor por dentro e por fora, so muda o tamanho). */
.sz-label-chip-outer {
  display: flex;
  align-items: center;
  flex: 1;
  width: 100%;
  min-width: 0;
  border-radius: 4px;
  box-sizing: border-box;
}
.sz-label-chip-outer.is-active {
  padding: 4px 8px;
  /* margin negativa pra "estourar" o padding do parent (px-2 py-1 do
     SidebarGroupLeaf, h-8) — wrapper ocupa o leaf inteiro, igual ao bg-n-alpha-2
     que aparece em items active de Canais. */
  margin: -4px -8px;
  width: calc(100% + 16px);
  background: var(--sz-chip-bg);
}

/* Chip dimensionado pra bater com Canais (font 14, leaf h-8=32px) */
.sz-label-chip {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 2px 8px;
  height: 28px;
  min-width: 0;
  flex: 1;
  border-radius: 14px;
  background: var(--sz-chip-bg);
  color: var(--sz-chip-accent);
  font-family: 'Inter', system-ui, sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
}

.sz-label-chip-inner {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  height: 20px;
}

.sz-label-chip-dot-wrap {
  width: 14px;
  height: 14px;
  position: relative;
  flex-shrink: 0;
}
.sz-label-chip-dot {
  position: absolute;
  left: calc(50% - 10px/2);
  top: calc(50% - 10px/2);
  width: 10px;
  height: 10px;
  background: var(--sz-chip-accent);
  border-radius: 8px;
}

.sz-label-chip-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sz-label-chip-counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 20px;
  padding: 0 6px;
  border-radius: 47px;
  background: var(--sz-chip-counter-bg);
  color: var(--sz-chip-accent);
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  flex-shrink: 0;
}
</style>

<!-- Bloco NAO-scoped: dark + neutralizacao do bg active do parent. -->
<style>
.dark .sz-label-chip {
  background: var(--sz-chip-bg-dark) !important;
  color: var(--sz-chip-accent-dark) !important;
}
.dark .sz-label-chip-dot {
  background: var(--sz-chip-accent-dark) !important;
}
.dark .sz-label-chip-counter {
  background: var(--sz-chip-counter-bg-dark) !important;
  color: var(--sz-chip-accent-dark) !important;
}
.dark .sz-label-chip-outer.is-active {
  background: var(--sz-chip-bg-dark) !important;
}

/* Neutraliza o bg-n-alpha-2 generico do parent quando ele contem chip:
   o destaque visual vem do .sz-label-chip-outer.is-active dentro. */
.active:has(.sz-label-chip),
a.active:has(.sz-label-chip) {
  background: transparent !important;
  color: inherit !important;
}
</style>
